import React, { useState, useEffect } from "react";
import {
  Title,
  Text,
  Card,
  Loader,
  Center,
  Alert,
  Button,
  Group,
  Stack,
  Divider,
  Modal,
  NumberInput,
  Select,
  Badge,
  Switch,
  SimpleGrid,
  Box,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconRefresh,
  IconArrowLeft,
  IconEdit,
  IconCreditCard,
  IconCalendar,
  IconCurrencyDollar,
  IconUserShield,
  IconUsers,
  IconReceipt,
} from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { getAdminApiEndpoint, setAdminCompanyCode } from "../../config/config";
import { adminHeaders } from "../../services/adminApi";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import PageContent from "../shared/PageContent/PageContent";
import { DateInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { handleGenericError } from "../../utils/errorHandler";

const AdminSubscriptionPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  // Invoice history state
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [invoiceHistoryLoading, setInvoiceHistoryLoading] = useState(true);

  // Modal form state
  const [editForm, setEditForm] = useState({
    currentSubscriptionStartDate: null,
    currentSubscriptionDate: null,
    nextSubscriptionDate: null,
    repRate: 0,
    managerRate: 0,
    preferredSubscriptionDayOfMonth: "1",
    isActive: false,
  });

  useEffect(() => {
    if (code) {
      // Keep the admin tools that aren't opened from a company page pointed at
      // the company being worked on.
      setAdminCompanyCode(code);
      fetchSubscriptionDetails();
      fetchInvoiceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/subscription/company`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch subscription details: ${response.status}`
        );
      }

      const result = await response.json();
      const data = result.data || result;
      setSubscription(data);

      // Parse UTC dates without timezone conversion helper
      const parseUTCDateLocal = dateString => {
        if (!dateString) return null;
        const date = new Date(dateString);
        // Create a new date using UTC values to avoid timezone conversion
        return new Date(
          date.getUTCFullYear(),
          date.getUTCMonth(),
          date.getUTCDate()
        );
      };

      // Initialize edit form with current values
      setEditForm({
        currentSubscriptionStartDate: parseUTCDateLocal(
          data.current_subscription_start_date
        ),
        currentSubscriptionDate: parseUTCDateLocal(
          data.current_subscription_date
        ),
        nextSubscriptionDate: parseUTCDateLocal(data.next_subscription_date),
        repRate: data.rep_rate || 0,
        managerRate: data.manager_rate || 0,
        preferredSubscriptionDayOfMonth:
          data.preferred_subscription_day_of_month?.toString() || "1",
        isActive: data.is_active ?? false,
      });
    } catch (err) {
      handleGenericError(err, "Error fetching subscription");
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoice history
  const fetchInvoiceHistory = async () => {
    try {
      setInvoiceHistoryLoading(true);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        return;
      }

      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/subscription/company/invoice-history`),
        {
          method: "GET",
          headers: adminHeaders(jwt, code),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch invoice history");
      }

      const result = await response.json();
      setInvoiceHistory(result.data || []);
    } catch (err) {
      handleGenericError(err, "Error fetching invoice history.");
      setInvoiceHistory([]);
    } finally {
      setInvoiceHistoryLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(`/client/${code}`);
  };

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  const formatCurrency = amount => {
    if (amount === null || amount === undefined) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const ordinal = n => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  /**
   * Formats a date string from the API into a readable format.
   * Expected input format: ISO date string (e.g., "2024-03-15T00:00:00.000Z")
   */
  function formatBillingDate(dateString) {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
    } catch (error) {
      return dateString;
    }
  }

  const handleEditSubmit = async () => {
    try {
      const jwt = await getVerifiedJWT();

      const payload = {
        cycle_start_date: editForm.currentSubscriptionStartDate,
        cycle_end_date: editForm.currentSubscriptionDate,
        rep_rate: editForm.repRate,
        manager_rate: editForm.managerRate,
        preferred_subscription_day_of_month: parseInt(
          editForm.preferredSubscriptionDayOfMonth,
          10
        ),
        is_active: editForm.isActive,
      };

      const response = await fetch(
        getAdminApiEndpoint(`/v2/admin/subscription/company`),
        {
          method: "PUT",
          headers: adminHeaders(jwt, code),
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Failed to update subscription: ${response.status}`
        );
      }

      showSuccess("Subscription updated successfully");
      setEditModalOpened(false);
      fetchSubscriptionDetails(); // Refresh data
    } catch (err) {
      handleGenericError(err, "Failed to update subscription");
    }
  };

  if (loading) {
    return (
      <PageContent
        title="Manage Subscription"
        subtitle="Loading subscription details..."
      >
        <Center style={{ height: 400 }}>
          <Stack align="center">
            <Loader size="lg" />
            <Text>Loading subscription details...</Text>
          </Stack>
        </Center>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent
        title="Manage Subscription"
        subtitle="Error loading subscription information"
      >
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error loading subscription details"
          color="red"
          withCloseButton={false}
        >
          <Text mb="md">{error}</Text>
          <Group gap="sm">
            <Button
              leftSection={<IconRefresh size={14} />}
              onClick={fetchSubscriptionDetails}
              color="red"
              variant="outline"
            >
              Retry
            </Button>
            <Button
              leftSection={<IconArrowLeft size={14} />}
              onClick={handleBackClick}
              variant="outline"
            >
              Back to Client
            </Button>
          </Group>
        </Alert>
      </PageContent>
    );
  }

  return (
    <PageContent
      title="Manage Subscription"
      subtitle={`Subscription management for ${subscription?.company_name || "client"}`}
    >
      <Group justify="space-between" mb="lg">
        <Button
          leftSection={<IconArrowLeft size={16} />}
          onClick={handleBackClick}
          variant="outline"
        >
          Back to Client
        </Button>
        <Button
          leftSection={<IconEdit size={16} />}
          onClick={() => setEditModalOpened(true)}
          variant="filled"
        >
          Edit Subscription
        </Button>
      </Group>

      {/* ── Current Cycle Summary ── */}
      <Card padding="lg" radius="md" withBorder mb={30}>
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Title order={3}>Current Cycle</Title>
            {subscription?.current_subscription_start_date &&
              subscription?.current_subscription_date && (
                <Text size="sm" c="dimmed">
                  {formatBillingDate(
                    subscription.current_subscription_start_date
                  )}{" "}
                  - {formatBillingDate(subscription.current_subscription_date)}
                </Text>
              )}
          </Group>

          <Divider />

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="lg">
            {/* Managers */}
            <Box>
              <Group gap="xs" mb="xs">
                <IconUserShield
                  size={18}
                  color="var(--mantine-color-violet-6)"
                />
                <Text size="sm" c="dimmed" fw={500}>
                  Managers
                </Text>
              </Group>
              <Text size={28} fw={700} lh={1.2}>
                {subscription.active_manager_count ?? 0}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                {formatCurrency(subscription.current_cycle_manager_amount ?? 0)}{" "}
                total
              </Text>
              {subscription?.current_cycle_prorated &&
                subscription.active_manager_count > 0 && (
                  <Text size="xs" c="green" mt={2} fw={500}>
                    {formatCurrency(
                      subscription.current_cycle_manager_amount /
                        subscription.active_manager_count
                    )}{" "}
                    prorated rate
                  </Text>
                )}
            </Box>

            {/* Reps */}
            <Box>
              <Group gap="xs" mb="xs">
                <IconUsers size={18} color="var(--mantine-color-blue-6)" />
                <Text size="sm" c="dimmed" fw={500}>
                  Reps
                </Text>
              </Group>
              <Text size={28} fw={700} lh={1.2}>
                {subscription.active_rep_count ?? 0}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                {formatCurrency(subscription.current_cycle_rep_amount ?? 0)}{" "}
                total
              </Text>
              {subscription?.current_cycle_prorated &&
                subscription.active_rep_count > 0 && (
                  <Text size="xs" c="green" mt={2} fw={500}>
                    {formatCurrency(
                      subscription.current_cycle_rep_amount /
                        subscription.active_rep_count
                    )}{" "}
                    prorated rate
                  </Text>
                )}
            </Box>

            {/* Total Users */}
            <Box>
              <Group gap="xs" mb="xs">
                <IconUsers size={18} color="var(--mantine-color-gray-6)" />
                <Text size="sm" c="dimmed" fw={500}>
                  Total Number
                </Text>
              </Group>
              <Text size={28} fw={700} lh={1.2}>
                {subscription.total_users ?? 0}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                active users
              </Text>
            </Box>

            {/* Total Cost */}
            <Box>
              <Group gap="xs" mb="xs">
                <IconReceipt size={18} color="var(--mantine-color-green-6)" />
                <Text size="sm" c="dimmed" fw={500}>
                  Total Cost
                </Text>
              </Group>
              <Text size={28} fw={700} lh={1.2} c="green">
                {formatCurrency(subscription.current_cycle_total_amount ?? 0)}
              </Text>
              <Text size="xs" c="dimmed" mt={4}>
                this cycle
              </Text>
            </Box>
          </SimpleGrid>
        </Stack>
      </Card>

      <Stack gap="lg">
        {/* Subscription Details Card */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Title order={2}>Subscription Details</Title>
          </Group>
          <Divider mb="md" />

          <Stack gap="md">
            {/* Subscription Status */}
            <Card
              padding="md"
              radius="md"
              withBorder
              bg={subscription?.is_active ? "green.0" : "red.0"}
            >
              <Group gap="xs" justify="space-between">
                <Text fw={500} size="sm" c="dimmed">
                  Subscription Status:
                </Text>
                <Badge
                  color={subscription?.is_active ? "green" : "red"}
                  size="lg"
                >
                  {subscription?.is_active ? "Active" : "Inactive"}
                </Badge>
              </Group>
            </Card>

            {/* Pricing Information */}
            <Group gap="xl" grow>
              <Card padding="md" radius="md" withBorder bg="blue.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCurrencyDollar
                      size={20}
                      color="var(--mantine-color-blue-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Rep Rate
                    </Text>
                  </Group>
                  <Text fw={700} size="xl">
                    {formatCurrency(subscription?.rep_rate)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    per rep per month
                  </Text>
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder bg="violet.0">
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCurrencyDollar
                      size={20}
                      color="var(--mantine-color-violet-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Manager Rate
                    </Text>
                  </Group>
                  <Text fw={700} size="xl">
                    {formatCurrency(subscription?.manager_rate)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    per manager per month
                  </Text>
                </Stack>
              </Card>
            </Group>

            <Divider my="xs" />

            {/* Billing Cycle Information */}
            <Group gap="xl" grow>
              <Card padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCalendar
                      size={20}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Current Subscription Start Date
                    </Text>
                  </Group>
                  <Text fw={600} size="lg">
                    {formatDate(subscription?.current_subscription_start_date)}
                  </Text>
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCalendar
                      size={20}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Current Subscription Date
                    </Text>
                  </Group>
                  <Group gap="xs" align="center">
                    <Text fw={600} size="lg">
                      {formatDate(subscription?.current_subscription_date)}
                    </Text>
                    {subscription?.current_cycle_prorated && (
                      <Badge color="orange" size="sm">
                        Prorated
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Card>
            </Group>

            <Group gap="xl" grow>
              <Card padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCalendar
                      size={20}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Next Subscription Date
                    </Text>
                  </Group>
                  <Group gap="xs" align="center">
                    <Text fw={600} size="lg">
                      {formatDate(subscription?.next_subscription_date)}
                    </Text>
                    {subscription?.next_cycle_prorated && (
                      <Badge color="orange" size="sm">
                        Prorated
                      </Badge>
                    )}
                  </Group>
                </Stack>
              </Card>

              <Card padding="md" radius="md" withBorder>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconCreditCard
                      size={20}
                      color="var(--mantine-color-gray-6)"
                    />
                    <Text fw={500} size="sm" c="dimmed">
                      Preferred Day of Month
                    </Text>
                  </Group>
                  <Text fw={600} size="lg">
                    {ordinal(
                      subscription?.preferred_subscription_day_of_month || 1
                    )}
                  </Text>
                </Stack>
              </Card>
            </Group>

            <Divider my="xs" />

            {/* Payment Method Information */}
            <Card padding="md" radius="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <IconCreditCard
                    size={20}
                    color="var(--mantine-color-gray-6)"
                  />
                  <Text fw={500} size="sm" c="dimmed">
                    Card Details
                  </Text>
                </Group>

                {subscription?.payment_method?.brand &&
                subscription?.payment_method?.last4 ? (
                  <>
                    <Text fw={600} size="lg">
                      {subscription.payment_method.brand.toUpperCase()} ••••{" "}
                      {subscription.payment_method.last4}
                    </Text>
                    {subscription?.payment_method?.warning && (
                      <Text size="sm" c="orange" fw={500}>
                        {subscription.payment_method.warning}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text size="sm" c="dimmed">
                    No card has been added yet.
                  </Text>
                )}
              </Stack>
            </Card>
          </Stack>
        </Card>

        {/* Additional Information (if needed) */}
        {subscription?.company_name && (
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">
              Company Information
            </Title>
            <Group gap="xs">
              <Text fw={500} c="dimmed" style={{ minWidth: 100 }}>
                Company:
              </Text>
              <Text>{subscription.company_name}</Text>
            </Group>
            {subscription?.company_id && (
              <Group gap="xs" mt="xs">
                <Text fw={500} c="dimmed" style={{ minWidth: 100 }}>
                  Company ID:
                </Text>
                <Text>{subscription.company_id}</Text>
              </Group>
            )}
          </Card>
        )}

        {/* ── Invoice History ── */}
        <Card padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <Title order={3}>Invoice History</Title>
            </Group>
            <Divider />

            {invoiceHistoryLoading ? (
              <Group justify="center" py="xl">
                <Loader size="md" />
              </Group>
            ) : invoiceHistory.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No invoice history available.
              </Text>
            ) : (
              <Box style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #dee2e6" }}>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Payment Date
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Period Start
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Period End
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Reps
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Managers
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Rep Rate
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Manager Rate
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Total
                      </th>
                      <th
                        style={{
                          padding: "12px 8px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#868e96",
                        }}
                      >
                        Completed
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceHistory.map((invoice, index) => (
                      <React.Fragment key={index}>
                        <tr
                          style={{
                            borderBottom: invoice.payment_failed_reason
                              ? "none"
                              : "1px solid #f1f3f5",
                          }}
                        >
                          <td style={{ padding: "12px 8px" }}>
                            <Text size="sm" fw={600}>
                              {invoice.payment_date
                                ? formatBillingDate(invoice.payment_date)
                                : "N/A"}
                            </Text>
                          </td>
                          <td style={{ padding: "12px 8px" }}>
                            <Text size="sm" c="dimmed">
                              {invoice.subscription_start_date
                                ? formatBillingDate(
                                    invoice.subscription_start_date
                                  )
                                : "N/A"}
                            </Text>
                          </td>
                          <td style={{ padding: "12px 8px" }}>
                            <Text size="sm" c="dimmed">
                              {invoice.subscription_end_date
                                ? formatBillingDate(
                                    invoice.subscription_end_date
                                  )
                                : "N/A"}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "right" }}
                          >
                            <Text size="sm">
                              {invoice.active_rep_count ?? 0}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "right" }}
                          >
                            <Text size="sm">
                              {invoice.active_manager_count ?? 0}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "right" }}
                          >
                            <Text size="sm" c="dimmed">
                              {formatCurrency(invoice.rep_rate ?? 0)}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "right" }}
                          >
                            <Text size="sm" c="dimmed">
                              {formatCurrency(invoice.manager_rate ?? 0)}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "right" }}
                          >
                            <Text size="sm" fw={600}>
                              {formatCurrency(invoice.total_amount ?? 0)}
                            </Text>
                          </td>
                          <td
                            style={{ padding: "12px 8px", textAlign: "center" }}
                          >
                            {invoice.payment_completed ? (
                              <Badge color="green" size="sm" variant="light">
                                Yes
                              </Badge>
                            ) : (
                              <Badge color="gray" size="sm" variant="light">
                                No
                              </Badge>
                            )}
                          </td>
                        </tr>
                        {invoice.payment_failed_reason && (
                          <tr style={{ borderBottom: "1px solid #f1f3f5" }}>
                            <td
                              colSpan={9}
                              style={{
                                padding: "8px 8px 12px 8px",
                                backgroundColor: "#fff5f5",
                              }}
                            >
                              <Group gap="xs" align="center">
                                <Badge color="red" size="sm" variant="filled">
                                  Payment Failed
                                </Badge>
                                <Text size="sm" c="red" fw={500}>
                                  {invoice.payment_failed_reason}
                                </Text>
                              </Group>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Edit Subscription Modal */}
      <Modal
        opened={editModalOpened}
        onClose={() => setEditModalOpened(false)}
        title="Edit Subscription Details"
        size="lg"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Update the subscription details for this client. Changes will take
            effect immediately.
          </Text>

          <Switch
            label="Active Billing"
            description="When enabled, billing is active for this company. When disabled, billing is paused."
            checked={editForm.isActive}
            onChange={event =>
              setEditForm({
                ...editForm,
                isActive: event.currentTarget.checked,
              })
            }
            size="md"
            color="green"
          />

          <Divider />

          {/* Pricing Rates */}
          <Group grow>
            <NumberInput
              label="Rep Rate"
              description="Monthly rate per rep"
              placeholder="10.00"
              value={editForm.repRate}
              onChange={value => setEditForm({ ...editForm, repRate: value })}
              leftSection={<IconCurrencyDollar size={16} />}
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              min={0}
              step={0.5}
            />
            <NumberInput
              label="Manager Rate"
              description="Monthly rate per manager"
              placeholder="20.00"
              value={editForm.managerRate}
              onChange={value =>
                setEditForm({ ...editForm, managerRate: value })
              }
              leftSection={<IconCurrencyDollar size={16} />}
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              min={0}
              step={0.5}
            />
          </Group>

          <Divider />

          {/* Billing Dates */}
          <Group grow>
            <DateInput
              label="Current Subscription Start Date"
              description="Current subscription cycle start"
              placeholder="Select date"
              value={editForm.currentSubscriptionStartDate}
              onChange={value =>
                setEditForm({
                  ...editForm,
                  currentSubscriptionStartDate: value,
                })
              }
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DateInput
              label="Current Subscription Date"
              description="Current subscription date"
              placeholder="Select date"
              value={editForm.currentSubscriptionDate}
              onChange={value =>
                setEditForm({ ...editForm, currentSubscriptionDate: value })
              }
              leftSection={<IconCalendar size={16} />}
              clearable
            />
          </Group>

          <Select
            label="Preferred Day of Month"
            description="Day of the month for recurring billing"
            leftSection={<IconCreditCard size={16} />}
            value={editForm.preferredSubscriptionDayOfMonth}
            onChange={value =>
              setEditForm({
                ...editForm,
                preferredSubscriptionDayOfMonth: value || "1",
              })
            }
            data={Array.from({ length: 28 }, (_, i) => ({
              value: String(i + 1),
              label: `${ordinal(i + 1)} of the month`,
            }))}
            searchable
          />

          <Divider />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setEditModalOpened(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              leftSection={<IconEdit size={16} />}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>
    </PageContent>
  );
};

export default AdminSubscriptionPage;
